import { useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { EVOTRUX_URL_HASH } from "constants/index";
import {
  useCarrierLanePreferences,
  useCarrierLanePreferencesUpdate,
  useRoleContext,
} from "hooks";

const LANE_PREFERENCES_ID = EVOTRUX_URL_HASH.CARRIER_LANE_PREFERENCES.slice(1);

export function CarrierPreferences() {
  const { role } = useRoleContext();
  const { preferences, error: fetchError } = useCarrierLanePreferences();
  const [matchOnlyWithPartners, setMatchOnlyWithPartners] = useState<
    boolean | null
  >(null);
  const [showUpdatedToast, setShowUpdatedToast] = useState(false);
  const hasSeededRef = useRef(false);
  const {
    updatePreferences,
    error: updateError,
    loading: updateLoading,
  } = useCarrierLanePreferencesUpdate({ preferences });

  useEffect(() => {
    if (preferences != null && !hasSeededRef.current) {
      setMatchOnlyWithPartners(preferences.match_only_with_partners);
      hasSeededRef.current = true;
    }
  }, [preferences]);

  if (role?.type !== "CARRIER") {
    return null;
  }

  const handlePartnersOnlyChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.checked;
    setMatchOnlyWithPartners(newValue);
    updatePreferences({ match_only_with_partners: newValue });
    setShowUpdatedToast(true);
  };

  const isLoading = preferences == null || matchOnlyWithPartners === null;

  return (
    <Box m="1rem" id={LANE_PREFERENCES_ID}>
      <Typography variant="h4">Notification Preferences</Typography>
      <EvotruxDivider />
      <Box mt="1rem">
        {isLoading ? (
          <Stack>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {/* The "notify me about broker shipments" switch is parked. Restore
            its markup and change handler from commit 860ea5fec (PR #2091) when
            the preference ships. */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  inputProps={
                    {
                      "data-testid": "match-only-with-partners-switch",
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  checked={matchOnlyWithPartners ?? false}
                  onChange={handlePartnersOnlyChange}
                  disabled={updateLoading}
                />
                <Tooltip title="Restrict lane matches to your confirmed partner shippers.">
                  <Typography variant="body1">
                    Only notify me about <u>partner</u> shipments
                  </Typography>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 7 }}>
                Your lanes will not match with shipments from non-partner
                shippers.
              </Typography>
            </Box>
          </Stack>
        )}
        <ErrorTypography error={fetchError} />
        <ErrorTypography error={updateError} />
      </Box>
      <Snackbar
        open={showUpdatedToast}
        onClose={() => setShowUpdatedToast(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowUpdatedToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Updated
        </Alert>
      </Snackbar>
    </Box>
  );
}
